import { useState } from 'react'
import { OnboardingFormSection } from './OnboardingFormSection'
import { industries, roles, useCases } from './constants'
import { useMutate } from 'restful-react'
import { useHistory } from 'react-router-dom'
import Loader from 'react-loader-spinner'
import useRootSnackbar from '@/services/useRootSnackbar'
import { cn } from '@/utlis/cn'
import { MuseLogo } from '@/components/MuseLogo'
import { PageIndicator } from '@/UIComponents2/PageIndicator'
import { Button } from '@/UIComponents2/Button'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import { useTranslation } from 'react-i18next'
import LeftArrowIcon from '@/CustomIcons/LeftArrowIcon'

export function OnboardingForm() {
    const RootSnackbar = useRootSnackbar()

    const history = useHistory()
    const { t } = useTranslation()

    const { mutate: updateUser } = useMutate({
        verb: 'PATCH',
        path: `/v1/users/_me`,
    })

    const [currentStep, setCurrentStep] = useState(0)

    const [selectedIndustries, setSelectedIndustries] = useState<string[]>([])
    const [otherIndustrySelected, setOtherIndustrySelected] = useState(false)
    const [otherIndustry, setOtherIndustry] = useState('')

    const [selectedRoles, setSelectedRoles] = useState<string[]>([])
    const [otherRoleSelected, setOtherRoleSelected] = useState(false)
    const [otherRole, setOtherRole] = useState('')

    const [selectedUseCases, setSelectedUseCases] = useState<string[]>([])
    const [otherUseCaseSelected, setOtherUseCaseSelected] = useState(false)
    const [otherUseCase, setOtherUseCase] = useState('')

    const [updateUserLoading, setUpdateUserLoading] = useState(false)

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            const industries = Array.from(new Set([...selectedIndustries, otherIndustry.toLowerCase()].filter(Boolean)))
            const roles = Array.from(new Set([...selectedRoles, otherRole.toLowerCase()].filter(Boolean)))
            const useCases = Array.from(new Set([...selectedUseCases, otherUseCase.toLowerCase()].filter(Boolean)))
            const updatePromise = updateUser({
                onboardingInfo: {
                    industries: industries.length > 0 ? industries : undefined,
                    roles: roles.length > 0 ? roles : undefined,
                    useCases: useCases.length > 0 ? useCases : undefined,
                },
            })
            if (currentStep === 2) {
                setUpdateUserLoading(true)
                await updatePromise
                history.push('/')
                RootSnackbar.show({
                    message: 'Your information has been saved successfully',
                    severity: 'success',
                })
                setUpdateUserLoading(false)
            } else {
                setCurrentStep((prev) => prev + 1)
            }
        } catch {
            RootSnackbar.show({
                message: 'Failed to save onboarding info',
                severity: 'error',
            })
        }
    }

    const handleSkip = (e: React.MouseEvent) => {
        e.preventDefault()
        history.push('/')
    }

    const bgStyle =
        "bg-[url('/images/onboarding-blur.webp')] bg-black bg-no-repeat bg-local overflow-x-hidden overflow-y-auto"

    return (
        <div className={cn('relative h-full flex items-center justify-center', bgStyle)}>
            <form className={cn('flex flex-col gap-[40px] p-[24px] h-full')} onSubmit={handleSubmit}>
                <div className="w-[108px] h-[24px]">
                    <MuseLogo />
                </div>
                <PageIndicator totalPages={3} currentPage={currentStep} onClick={(step) => setCurrentStep(step)} />

                {currentStep === 0 && (
                    <OnboardingFormSection
                        title="What best describes you?"
                        entries={roles}
                        selectedEntries={selectedRoles}
                        setSelectedEntries={setSelectedRoles}
                        otherEntry={otherRole}
                        setOtherEntry={setOtherRole}
                        otherEntrySelected={otherRoleSelected}
                        setOtherEntrySelected={setOtherRoleSelected}
                    />
                )}

                {currentStep === 1 && (
                    <OnboardingFormSection
                        title="How do you plan on using Muse?"
                        entries={useCases}
                        selectedEntries={selectedUseCases}
                        setSelectedEntries={setSelectedUseCases}
                        otherEntry={otherUseCase}
                        setOtherEntry={setOtherUseCase}
                        otherEntrySelected={otherUseCaseSelected}
                        setOtherEntrySelected={setOtherUseCaseSelected}
                    />
                )}

                {currentStep === 2 && (
                    <OnboardingFormSection
                        title="What industry are you in?"
                        itemType="bubble"
                        entries={industries}
                        selectedEntries={selectedIndustries}
                        setSelectedEntries={setSelectedIndustries}
                        otherEntry={otherIndustry}
                        setOtherEntry={setOtherIndustry}
                        otherEntrySelected={otherIndustrySelected}
                        setOtherEntrySelected={setOtherIndustrySelected}
                    />
                )}

                <div className="flex flex-col gap-[16px]">
                    <div className="flex gap-[16px]">
                        {currentStep > 0 && (
                            <Button
                                className="relative bg-[rgba(255,255,255,0.03)] min-w-[120px] basis-[150px]"
                                variant="secondary"
                                onClick={(e) => {
                                    e.preventDefault()
                                    setCurrentStep((prev) => prev - 1)
                                }}
                                disabled={currentStep === 0 || updateUserLoading}
                            >
                                <LeftArrowIcon className="absolute left-4" />
                                {t('Back')}
                            </Button>
                        )}
                        <Button
                            type="submit"
                            disabled={updateUserLoading}
                            variant="primary"
                            onClick={handleSubmit}
                            className="basis-[300px] min-w-[160px] relative"
                        >
                            {updateUserLoading ? (
                                <Loader type="TailSpin" color="black" height={24} width={24} timeout={300000} />
                            ) : (
                                <>
                                    {t('Next Step')}
                                    <RightArrowIcon className="absolute right-4" />
                                </>
                            )}
                        </Button>
                    </div>
                    <a
                        onClick={handleSkip}
                        className="flex-none w-[150px] pl-[16px] text-muse-fg/75 pb-[24px] pt-[8px] cursor-pointer hover:text-white"
                    >
                        {t('Skip for now')}
                    </a>
                </div>
            </form>
        </div>
    )
}
